"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown } from "lucide-react";
import { usePathname } from "next/navigation";

interface DropdownLink {
  key: string;
  value: string;
}

interface CardDropdownProps {
  dropdownLinks?: DropdownLink[];
  dropdownPlaceholder?: string;
  className?: string;
}

const STORAGE_KEY = "landing_utm";

export const CardDropdown: React.FC<CardDropdownProps> = ({
  dropdownLinks = [],
  dropdownPlaceholder = "Choose One",
  className = "",
}) => {
  const pathname = usePathname();

  if (!dropdownLinks || dropdownLinks.length === 0) {
    return null;
  }

  const handleDropdownChange = (value: string) => {
    // Ensure the URL has a protocol for external links
    const url = value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')
      ? value
      : `https://${value}`;

    // Track dropdown link click if UTMs exist
    if (typeof window !== "undefined") {
      try {
        const storedUtms = sessionStorage.getItem(STORAGE_KEY);
        if (storedUtms && window.gtag) {
          const utmData = JSON.parse(storedUtms);
          const selectedLink = dropdownLinks.find(link => link.value === value);

          window.gtag("event", "content_link_click", {
            link_url: url,
            link_text: selectedLink?.key || url,
            page_path: pathname,
            link_type: "dropdown",
            ...utmData,
          });
        }
      } catch (error) {
        console.error("Failed to track dropdown click:", error);
      }
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={className}>
      <Select onValueChange={handleDropdownChange}>
        <SelectTrigger className="bg-transparent border-current text-current focus:ring-0 focus:ring-offset-0 relative pr-10 [&>svg]:hidden">
          <SelectValue placeholder={dropdownPlaceholder} />
        </SelectTrigger>
        <ChevronDown className="h-4 w-4 text-gold absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
        <SelectContent
          className="z-[9999999999999] border shadow-md bg-white py-6"
          position="popper"
          side="bottom"
          align="start"
        >
          {dropdownLinks.map((link, index) => (
            <SelectItem key={index} value={link.value}>
              {link.key}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};